import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, RefreshCw, Eye, EyeOff } from 'lucide-react';
import {
  fetchAllEditorialContent,
  createEditorialItem,
  updateEditorialItem,
  deleteEditorialItem,
} from '../services/editorialContentApi';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

const emptyForm = {
  url: '',
  title: '',
  description: '',
  contributor_label: '',
  designer_id: '',
  tags: 'watch',
  sort_order: 0,
  published: false,
};

export default function AdminEditorialContent() {
  const [items, setItems] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/designers/public`)
      .then((r) => r.json())
      .then((d) => setDesigners(d.designers || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchAllEditorialContent();
      setItems(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) return;
    setSaving(true);
    try {
      await createEditorialItem({
        url: form.url.trim(),
        title: form.title || undefined,
        description: form.description || undefined,
        contributor_label: form.contributor_label || undefined,
        designer_id: form.designer_id || undefined,
        tags: form.tags,
        sort_order: Number(form.sort_order) || 0,
        published: form.published,
      });
      setForm(emptyForm);
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    try {
      await updateEditorialItem(item.id, { published: !item.published });
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this editorial item?')) return;
    try {
      await deleteEditorialItem(id);
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading && !items.length) {
    return (
      <div className="flex items-center gap-2 text-gray-600">
        <RefreshCw className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Public marketing content</h3>
        <p className="text-sm text-gray-600 mt-1">
          YouTube and Instagram links for the homeowner site. Separate from plant records and designer tools. URLs must
          be from allowed domains (YouTube, Instagram).
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
      )}

      <form onSubmit={handleCreate} className="bg-white border border-gray-200 rounded-xl p-6 space-y-4 max-w-2xl">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <Plus className="w-5 h-5" /> Add item
        </h4>
        <div>
          <label className="block text-sm font-medium text-gray-700">URL (required)</label>
          <input
            type="url"
            value={form.url}
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="https://www.youtube.com/watch?v=… or Instagram permalink"
            required
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Link to designer profile</label>
            <select
              value={form.designer_id}
              onChange={(e) => {
                const d = designers.find((x) => x.id === e.target.value);
                setForm((f) => ({
                  ...f,
                  designer_id: e.target.value,
                  contributor_label: d ? (d.display_name || d.full_name || f.contributor_label) : f.contributor_label,
                }));
              }}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">-- No designer link --</option>
              {designers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.display_name || d.full_name}{d.business_name ? ` (${d.business_name})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contributor label</label>
            <input
              type="text"
              value={form.contributor_label}
              onChange={(e) => setForm((f) => ({ ...f, contributor_label: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g. John Connors (auto-filled when you pick a designer)"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={2}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Tags (comma-separated)</label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="homepage, watch, community"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use <strong>homepage</strong> for the home page video strip, <strong>watch</strong> for Watch &amp; tips,{' '}
              <strong>community</strong> for Community. Items can have multiple tags.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Sort order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
          />
          <span className="text-sm text-gray-700">Published (visible on public site)</span>
        </label>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Create'}
        </button>
      </form>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">All items ({items.length})</h4>
          <button
            type="button"
            onClick={() => load()}
            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 font-medium text-gray-700">Title / URL</th>
                <th className="text-left p-3 font-medium text-gray-700">Tags</th>
                <th className="text-left p-3 font-medium text-gray-700">Sort</th>
                <th className="text-left p-3 font-medium text-gray-700">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="p-3 max-w-md">
                    <div className="font-medium text-gray-900 truncate">{row.title || '(no title)'}</div>
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline break-all text-xs"
                    >
                      {row.url}
                    </a>
                  </td>
                  <td className="p-3 text-gray-600">{(row.tags || []).join(', ')}</td>
                  <td className="p-3">{row.sort_order}</td>
                  <td className="p-3">
                    <button
                      type="button"
                      onClick={() => togglePublish(row)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        row.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {row.published ? (
                        <>
                          <Eye className="w-3 h-3" /> Live
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3" /> Draft
                        </>
                      )}
                    </button>
                  </td>
                  <td className="p-3">
                    <button
                      type="button"
                      onClick={() => remove(row.id)}
                      className="text-red-600 hover:bg-red-50 p-2 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && !loading && (
            <p className="p-6 text-center text-gray-500 text-sm">No editorial items yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
